import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRoles } from '@/hooks/use-user-roles';
import { toast } from 'sonner';

export interface VerificationRequest {
  id: string;
  user_id: string;
  artwork_url: string;
  proof_url: string;
  status: 'pending' | 'approved' | 'rejected';
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  profiles?: {
    username: string;
    profile_image_url: string | null;
  };
}

/**
 * Hook to check if the current user is verified
 */
export const useIsVerified = () => {
  const { user, profile } = useAuth();

  return {
    isVerified: profile?.is_verified_human ?? false,
    isLoading: !profile && !!user,
  };
};

/**
 * Hook to get the current user's verification request status
 */
export const useVerificationStatus = () => {
  const { user } = useAuth();

  const { data: request, isLoading } = useQuery({
    queryKey: ['verification-status', user?.id],
    queryFn: async () => {
      if (!user) return null;

      const { data, error } = await supabase
        .from('verification_requests')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as VerificationRequest | null;
    },
    enabled: !!user,
  });

  return {
    request,
    isLoading,
    hasPendingRequest: request?.status === 'pending',
    isRejected: request?.status === 'rejected',
  };
};

/**
 * Hook to submit a verification request
 */
export const useSubmitVerification = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      artworkUrl,
      proofUrl,
    }: {
      artworkUrl: string;
      proofUrl: string;
    }) => {
      if (!user) throw new Error('Must be logged in');

      // Check if user already has a pending request
      const { data: existing } = await supabase
        .from('verification_requests')
        .select('id')
        .eq('user_id', user.id)
        .eq('status', 'pending')
        .maybeSingle();

      if (existing) {
        throw new Error('You already have a pending verification request');
      }

      const { data, error } = await supabase
        .from('verification_requests')
        .insert({
          user_id: user.id,
          artwork_url: artworkUrl,
          proof_url: proofUrl,
          status: 'pending',
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['verification-status'] });
      toast.success('Verification request submitted! We\'ll review it shortly.');
    },
    onError: (error: any) => {
      console.error('Error submitting verification:', error);
      toast.error(error.message || 'Failed to submit verification request');
    },
  });
};

/**
 * Hook for admins to fetch all pending verification requests
 */
export const usePendingVerifications = () => {
  const { user } = useAuth();
  const { isAdmin } = useUserRoles();

  return useQuery({
    queryKey: ['pending-verifications'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('verification_requests')
        .select(`
          *,
          profiles:user_id (
            username,
            profile_image_url
          )
        `)
        .eq('status', 'pending')
        .order('created_at', { ascending: true});

      if (error) throw error;
      return data as VerificationRequest[];
    },
    enabled: !!user && isAdmin,
  });
};

/**
 * Hook for admins to approve/reject verification requests
 */
export const useReviewVerification = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      requestId,
      approve,
      rejectionReason,
    }: {
      requestId: string;
      approve: boolean;
      rejectionReason?: string;
    }) => {
      if (!user) throw new Error('Must be logged in');

      const { data, error } = await supabase
        .from('verification_requests')
        .update({
          status: approve ? 'approved' : 'rejected',
          reviewed_by: user.id,
          reviewed_at: new Date().toISOString(),
          rejection_reason: rejectionReason || null,
        })
        .eq('id', requestId)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: (_, { approve }) => {
      queryClient.invalidateQueries({ queryKey: ['pending-verifications'] });
      toast.success(approve ? 'User verified successfully!' : 'Request rejected');
    },
    onError: (error: any) => {
      console.error('Error reviewing verification:', error);
      toast.error('Failed to review verification request');
    },
  });
};
